import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, GraduationCap, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function NotFound() {
  useSEO({ title: "Page not found | Training Basket", noindex: true });
  const location = useLocation();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const path = location.pathname;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/redirect?path=${encodeURIComponent(path)}`);
        if (data?.found && data.to) {
          api.post("/redirect-hit", { path }).catch(() => {});
          window.location.replace(data.to + window.location.search + window.location.hash);
          return;
        }
      } catch { /* resolver unavailable — fall through to 404 */ }
      if (!cancelled) {
        api.post("/log-404", { path, referrer: document.referrer }).catch(() => {});
        setState("missing");
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (state === "checking") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="notfound-checking">
        <Loader2 className="w-7 h-7 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-20" data-testid="notfound-page">
      <div className="text-center max-w-lg">
        <p className="text-7xl sm:text-8xl font-extrabold tracking-tight text-[#17263B]" data-testid="notfound-code">4<span className="text-[#F5B301]">0</span>4</p>
        <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-4" data-testid="notfound-title">This page moved or never existed</h1>
        <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed">
          The link may be old or mistyped. Try one of these instead — or talk to a counselor and we'll point you the right way.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <Link to="/" data-testid="notfound-home-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-bold px-6 py-3 text-sm transition-all hover:-translate-y-0.5">
            <Home className="w-4 h-4" /> Back to home
          </Link>
          <Link to="/courses" data-testid="notfound-courses-link"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 text-slate-700 font-bold px-6 py-3 text-sm hover:border-amber-300 transition-colors">
            <GraduationCap className="w-4 h-4 text-amber-500" /> Browse programs
          </Link>
        </div>
      </div>
    </div>
  );
}
